import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { S3uploaderService } from '../../services/s3uploader/s3uploader.service';
import { SupportAttachment } from '../../common/general-types/support-ticket';
import {
  isAllowedSupportAttachment,
  isImageAttachment,
  MAX_SUPPORT_ATTACHMENTS,
  SUPPORT_ATTACHMENT_ACCEPT,
} from '../../common/support-attachment.util';

@Component({
  selector: 'app-support-attachment-input',
  templateUrl: './support-attachment-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportAttachmentInputComponent {
  readonly attachments = input<SupportAttachment[]>([]);
  readonly attachmentsChange = output<SupportAttachment[]>();
  /** `icon` renders a paperclip control for embedding inside a reply composer. */
  readonly variant = input<'button' | 'icon'>('button');

  readonly uploading = signal(false);
  readonly error = signal('');
  readonly accept = SUPPORT_ATTACHMENT_ACCEPT;
  readonly maxAttachments = MAX_SUPPORT_ATTACHMENTS;

  private readonly uploadService = inject(S3uploaderService);

  async onFileSelected(event: Event): Promise<void> {
    const inputEl = event.target as HTMLInputElement;
    const file = inputEl.files?.item(0);
    inputEl.value = '';

    if (!file) {
      return;
    }

    this.error.set('');

    if (this.attachments().length >= MAX_SUPPORT_ATTACHMENTS) {
      this.error.set(`You can attach up to ${MAX_SUPPORT_ATTACHMENTS} files.`);
      return;
    }

    if (!isAllowedSupportAttachment(file)) {
      this.error.set('Only images and PDF files up to 10MB are allowed.');
      return;
    }

    this.uploading.set(true);

    try {
      const uploaded = isImageAttachment(file.type)
        ? await this.uploadService.secureImageUpload(file, 'support-attachments')
        : await this.uploadService.secureDocumentUpload(file, 'support-attachments');

      if (!uploaded) {
        this.error.set('Upload failed. Please try again.');
        return;
      }

      const next = [
        ...this.attachments(),
        {
          fileName: file.name,
          fileType: file.type,
          url: uploaded.fullUrl,
          key: uploaded.key,
        },
      ];
      this.attachmentsChange.emit(next);
    } catch {
      this.error.set('Upload failed. Please try again.');
    } finally {
      this.uploading.set(false);
    }
  }

  removeAttachment(attachment: SupportAttachment): void {
    this.attachmentsChange.emit(this.attachments().filter((item) => item.key !== attachment.key));
  }

  isImage(fileType: string): boolean {
    return isImageAttachment(fileType);
  }
}
